import { request } from './request'
import type { PageResult } from '@jiaofa/api-types'

export interface AdminMemberProfile {
  id: string
  userId: string
  nickname?: string
  phone?: string
  levelCode: string
  levelName?: string
  growthValue: number
  points: number
  balance: number
  totalPoints: number
  totalRecharge: number
  memberExpireAt?: string | null
  joinedAt?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface MemberProfilePayload {
  userId?: string
  levelCode?: string
  growthValue?: number
  points?: number
  balance?: number
  totalPoints?: number
  totalRecharge?: number
  memberExpireAt?: string | null
  joinedAt?: string | null
}

export interface AdminMembersParams {
  page?: number
  pageSize?: number
  keyword?: string
  levelCode?: string
}

export interface MemberLevelConfig {
  id?: string
  code: string
  name?: string
  growthThreshold?: number
  discountRate?: number | string
  pointsMultiplier?: number | string
  badge?: string
  sort?: number
}

export interface MemberLevelSavePayload {
  code: string
  name: string
  growthThreshold?: number
  discountRate?: number | string
  pointsMultiplier?: number | string
  badge?: string
  sort?: number
}

export interface GrowthRuleConfig {
  id?: string
  ruleType: string
  param?: string
  enabled?: boolean
}

export interface PointsRuleConfig {
  id?: string
  ruleType: string
  calcMethod?: string
  value?: number
  maxUsePerOrder?: number
  enabled?: boolean
}

export interface MemberBenefitConfig {
  id?: string
  levelCode: string
  benefitType?: string
  config?: string
  enabled?: boolean
  sort?: number
}

export const memberApiReady = true as const

export const fetchMembers = async (params: AdminMembersParams = {}): Promise<PageResult<AdminMemberProfile>> =>
  request<PageResult<AdminMemberProfile>>({
    url: '/members',
    method: 'GET',
    params,
  })

export const fetchMemberProfile = async (id: string): Promise<AdminMemberProfile> =>
  request<AdminMemberProfile>({
    url: `/members/${id}`,
    method: 'GET',
  })

export const fetchUserMember = async (userId: string): Promise<AdminMemberProfile> =>
  request<AdminMemberProfile>({
    url: `/users/${userId}/member`,
    method: 'GET',
  })

export const createMemberProfile = async (payload: MemberProfilePayload): Promise<AdminMemberProfile> =>
  request<AdminMemberProfile, MemberProfilePayload>({
    url: '/members',
    method: 'POST',
    data: payload,
  })

export const createUserMember = async (userId: string, payload: Omit<MemberProfilePayload, 'userId'> = {}): Promise<AdminMemberProfile> =>
  request<AdminMemberProfile, Omit<MemberProfilePayload, 'userId'>>({
    url: `/users/${userId}/member`,
    method: 'POST',
    data: payload,
  })

export const updateMemberProfile = async (id: string, payload: MemberProfilePayload): Promise<AdminMemberProfile> =>
  request<AdminMemberProfile, MemberProfilePayload>({
    url: `/members/${id}`,
    method: 'PUT',
    data: payload,
  })

export const updateUserMember = async (userId: string, payload: MemberProfilePayload): Promise<AdminMemberProfile> =>
  request<AdminMemberProfile, MemberProfilePayload>({
    url: `/users/${userId}/member`,
    method: 'PUT',
    data: payload,
  })

export const deleteMemberProfile = async (id: string): Promise<void> =>
  request<void>({
    url: `/members/${id}`,
    method: 'DELETE',
  })

export const deleteUserMember = async (userId: string): Promise<void> =>
  request<void>({
    url: `/users/${userId}/member`,
    method: 'DELETE',
  })

export const fetchMemberLevels = async (): Promise<MemberLevelConfig[]> =>
  request<MemberLevelConfig[]>({
    url: '/member-levels',
    method: 'GET',
  })

export const fetchMemberLevel = async (id: string): Promise<MemberLevelConfig> =>
  request<MemberLevelConfig>({
    url: `/member-levels/${id}`,
    method: 'GET',
  })

export const createMemberLevel = async (payload: MemberLevelSavePayload): Promise<MemberLevelConfig> =>
  request<MemberLevelConfig, MemberLevelSavePayload>({
    url: '/member-levels',
    method: 'POST',
    data: payload,
  })

export const updateMemberLevel = async (id: string, payload: MemberLevelSavePayload): Promise<MemberLevelConfig> =>
  request<MemberLevelConfig, MemberLevelSavePayload>({
    url: `/member-levels/${id}`,
    method: 'PUT',
    data: payload,
  })

export const deleteMemberLevel = async (id: string): Promise<void> =>
  request<void>({
    url: `/member-levels/${id}`,
    method: 'DELETE',
  })

export const updateMemberLevels = async (payload: MemberLevelConfig[]): Promise<void> =>
  request<void, MemberLevelConfig[]>({
    url: '/member-levels',
    method: 'PUT',
    data: payload,
  })

export const fetchGrowthRules = async (): Promise<GrowthRuleConfig[]> =>
  request<GrowthRuleConfig[]>({
    url: '/growth-rules',
    method: 'GET',
  })

export const updateGrowthRules = async (payload: GrowthRuleConfig[]): Promise<void> =>
  request<void, GrowthRuleConfig[]>({
    url: '/growth-rules',
    method: 'PUT',
    data: payload,
  })

export const fetchPointsRules = async (): Promise<PointsRuleConfig[]> =>
  request<PointsRuleConfig[]>({
    url: '/points-rules',
    method: 'GET',
  })

export const updatePointsRules = async (payload: PointsRuleConfig[]): Promise<void> =>
  request<void, PointsRuleConfig[]>({
    url: '/points-rules',
    method: 'PUT',
    data: payload,
  })

export const fetchMemberBenefits = async (): Promise<MemberBenefitConfig[]> =>
  request<MemberBenefitConfig[]>({
    url: '/member-benefits',
    method: 'GET',
  })

export const updateMemberBenefits = async (payload: MemberBenefitConfig[]): Promise<void> =>
  request<void, MemberBenefitConfig[]>({
    url: '/member-benefits',
    method: 'PUT',
    data: payload,
  })
